import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { progressAPI } from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";

const Progress = () => {
  usePageTitle("My Progress");
  const [activeTab, setActiveTab] = useState("all");
  const [allProgress, setAllProgress] = useState([]);
  const [inProgress, setInProgress] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    setLoading(true);
    try {
      const [allRes, inProgressRes, completedRes] = await Promise.all([
        progressAPI.getUserProgress(),
        progressAPI.getInProgressLists(),
        progressAPI.getCompletedLists(),
      ]);

      setAllProgress(allRes.data);
      setInProgress(inProgressRes.data);
      setCompleted(completedRes.data);
    } catch (error) {
      console.error("Failed to load progress:", error);
    } finally {
      setLoading(false);
    }
  };

  const getProgressData = () => {
    switch (activeTab) {
      case "in-progress":
        return inProgress;
      case "completed":
        return completed;
      default:
        return allProgress;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-white mb-8">My Progress</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
          <h3 className="text-gray-400 text-sm mb-2">Total Lists</h3>
          <p className="text-3xl font-bold text-white">{allProgress.length}</p>
        </div>
        <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
          <h3 className="text-gray-400 text-sm mb-2">In Progress</h3>
          <p className="text-3xl font-bold text-yellow-400">
            {inProgress.length}
          </p>
        </div>
        <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
          <h3 className="text-gray-400 text-sm mb-2">Completed</h3>
          <p className="text-3xl font-bold text-green-400">
            {completed.length}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-gray-700">
        <button
          onClick={() => setActiveTab("all")}
          className={`pb-3 px-4 font-semibold transition ${
            activeTab === "all"
              ? "text-blue-400 border-b-2 border-blue-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          All ({allProgress.length})
        </button>
        <button
          onClick={() => setActiveTab("in-progress")}
          className={`pb-3 px-4 font-semibold transition ${
            activeTab === "in-progress"
              ? "text-yellow-400 border-b-2 border-yellow-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          In Progress ({inProgress.length})
        </button>
        <button
          onClick={() => setActiveTab("completed")}
          className={`pb-3 px-4 font-semibold transition ${
            activeTab === "completed"
              ? "text-green-400 border-b-2 border-green-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Completed ({completed.length})
        </button>
      </div>

      {/* Progress List */}
      {getProgressData().length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          No lists found.{" "}
          <Link to="/" className="text-blue-400 hover:underline">
            Start exploring!
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {getProgressData().map((progress) => (
            <Link
              key={progress.id}
              to={`/list/${progress.gameListSlug}`}
              className="block bg-dark-card hover:bg-dark-hover border border-gray-700 rounded-lg p-6 transition"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-white">
                  {progress.gameListTitle}
                </h3>
                {progress.isCompleted && (
                  <span className="px-3 py-1 bg-green-600/20 text-green-400 text-sm rounded">
                    ✓ Completed
                  </span>
                )}
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span>
                    {progress.totalGuessed} / {progress.totalItems} guessed
                  </span>
                  <span>
                    {Math.round(
                      (progress.totalGuessed / progress.totalItems) * 100
                    )}
                    %
                  </span>
                </div>
                <div className="w-full bg-dark-bg rounded-full h-2 border border-gray-600">
                  <div
                    className={`h-full rounded-full transition-all ${
                      progress.isCompleted ? "bg-green-600" : "bg-blue-600"
                    }`}
                    style={{
                      width: `${
                        (progress.totalGuessed / progress.totalItems) * 100
                      }%`,
                    }}
                  ></div>
                </div>
              </div>

              {/* Dates */}
              <div className="flex justify-between text-sm text-gray-400">
                <span>Started: {formatDate(progress.startedAt)}</span>
                {progress.completedAt ? (
                  <span>Completed: {formatDate(progress.completedAt)}</span>
                ) : (
                  <span>Last guess: {formatDate(progress.lastGuessedAt)}</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Progress;
